import { useState } from "react";
import { api } from "../api";
import type { ConversationEntry } from "../types";
import { fmtTs } from "../utils";

export function ConversationsPanel({
  customerId,
  entries,
  directions,
  channels,
  onChanged,
  flash,
}: {
  customerId: string;
  entries: ConversationEntry[];
  directions: string[];
  channels: string[];
  onChanged: () => Promise<void>;
  flash: (kind: "info" | "error", msg: string) => void;
}) {
  const [text, setText] = useState("");
  const [direction, setDirection] = useState<string>(directions[0] ?? "note");
  const [channel, setChannel] = useState<string>(channels[0] ?? "other");
  const [tags, setTags] = useState("");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.addConversation(customerId, {
        text: text.trim(),
        direction,
        channel,
        tags: tags
          .split(/[,，\s]+/)
          .map((t) => t.trim())
          .filter(Boolean),
      });
      setText("");
      setTags("");
      await onChanged();
    } catch (err: unknown) {
      flash("error", (err as Error).message);
    }
  };

  const remove = async (id: string) => {
    if (!confirm("删除这条交流记录？")) return;
    try {
      await api.removeConversation(customerId, id);
      await onChanged();
    } catch (err: unknown) {
      flash("error", (err as Error).message);
    }
  };

  const sorted = [...entries].sort((a, b) => b.ts - a.ts);

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_2fr]">
      <section className="card px-5 py-4">
        <h3 className="text-sm font-semibold">记录一条新交流</h3>
        <form onSubmit={submit} className="mt-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">方向</label>
              <select className="select" value={direction} onChange={(e) => setDirection(e.target.value)}>
                {directions.map((d) => (
                  <option key={d} value={d}>
                    {labelOf(d)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">渠道</label>
              <select className="select" value={channel} onChange={(e) => setChannel(e.target.value)}>
                {channels.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label">正文</label>
            <textarea
              className="textarea h-32"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="详细记录这次交流：客户提出的需求、抱怨、自己回复 / 备忘的事…"
              required
            />
          </div>
          <div>
            <label className="label">标签（逗号 / 空格 分隔，可选）</label>
            <input
              className="input"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="续费 退款 抱怨 ..."
            />
          </div>
          <button type="submit" className="btn-primary w-full">
            保存这条记录
          </button>
        </form>
      </section>

      <section className="space-y-3">
        {sorted.length === 0 && (
          <div className="card px-5 py-8 text-center text-sm text-ink-500">
            还没有任何交流记录。所有记录都会落到{" "}
            <code className="font-mono">customers/{customerId}/conversations.jsonl</code>，可以提交 git。
          </div>
        )}
        {sorted.map((e) => (
          <article key={e.id} className="card px-5 py-3">
            <header className="mb-1 flex items-center justify-between text-xs text-ink-500">
              <div className="flex items-center gap-2">
                <span
                  className={
                    e.direction === "inbound"
                      ? "chip bg-blue-50 text-blue-700"
                      : e.direction === "outbound"
                        ? "chip bg-indigo-50 text-indigo-700"
                        : "chip bg-ink-100 text-ink-700"
                  }
                >
                  {labelOf(e.direction)}
                </span>
                <span className="chip">{e.channel}</span>
                {e.tags?.map((t) => (
                  <span key={t} className="chip bg-amber-50 text-amber-700">
                    {t}
                  </span>
                ))}
                <span>{fmtTs(e.ts)}</span>
              </div>
              <button className="text-ink-300 hover:text-red-600" onClick={() => remove(e.id)} title="删除">
                ×
              </button>
            </header>
            <p className="whitespace-pre-wrap text-sm text-ink-800">{e.text}</p>
          </article>
        ))}
      </section>
    </div>
  );
}

function labelOf(direction: string): string {
  switch (direction) {
    case "inbound":
      return "客户来信";
    case "outbound":
      return "我方回复";
    case "note":
      return "备忘";
    default:
      return direction;
  }
}
